import React from "react";
import "./DashboardEmptyState.scss";

const LEARN_HREF = "https://thebuyerlistingservice.com/guides";

// Skeleton of the real dashboard table - gray placeholder shapes only (no hard
// data, no color), faded behind the card so agents see the populated layout.
// Mirrors the live columns: date, address (thumb + name), matches, price,
// status, type, client, actions.
const SKELETON_ROW_COUNT = 7;

const HouseGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1v-9.5Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const BroadcastGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="1.9" fill="currentColor" />
    <path
      d="M8.6 8.6a5 5 0 0 0 0 6.8M15.4 15.4a5 5 0 0 0 0-6.8M6 6a9 9 0 0 0 0 12M18 18a9 9 0 0 0 0-12"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PeopleGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M3.5 19a5.5 5.5 0 0 1 11 0M16 5.3a3 3 0 0 1 0 5.4M16.5 19a5.5 5.5 0 0 0-2.3-4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const VARIANTS = {
  inventory: {
    eyebrow: "My Properties",
    title: "Gauge demand insights for any property",
    subtitle:
      "Save any property to My Properties and let buyer demand come to you: matches, alerts and reports, automatically.",
    Glyph: HouseGlyph,
    proofBadge: "24 matches",
    proofTone: "green",
    proofLive: true,
    skeletonCols: {
      grid: "58px minmax(0, 2.2fr) 96px 90px 88px 66px minmax(0, 1.5fr) 18px",
      cells: [
        { type: "bar", w: "60%" },
        { type: "addr" },
        { type: "pill" },
        { type: "bar", w: "72%" },
        { type: "bar", w: "60%" },
        { type: "tag" },
        { type: "bar", w: "80%" },
        { type: "kebab" },
      ],
    },
    steps: [
      {
        label: "Save any property",
        text: "A pre-listing seller, a FSBO, an expired listing, anything you're working on.",
      },
      {
        label: "Get alerted",
        text: "We notify you as buyer demand matches the properties in your inventory.",
      },
      {
        label: "Win the listing",
        text: "View demand insights, download a report, and bring real data to your prospective seller client.",
      },
    ],
    ctaLabel: "Add Prospect's Property",
    learnHref: LEARN_HREF,
    footnote: (
      <>
        Each home you save to My Properties gets{" "}
        <strong>its own real-time buyer demand and match&nbsp;alerts.</strong>
      </>
    ),
  },
  wantlisting: {
    eyebrow: "Buyer Want-Listings",
    title: "Publish a Buyer Want\u2011Listing",
    subtitle:
      "A Buyer Want-Listing puts the home you're looking for in front of the whole network. It's posted the same way you list a property for sale, then matched against active and coming-soon for-sale listings in the MLS.",
    Glyph: BroadcastGlyph,
    proofBadge: "Live to your network",
    proofTone: "blue",
    proofLive: true,
    skeletonCols: {
      grid: "58px minmax(0, 1.9fr) minmax(0, 1.4fr) 86px 104px 66px minmax(0, 1.4fr) 18px",
      cells: [
        { type: "bar", w: "60%" },
        { type: "bar", w: "88%" },
        { type: "loc" },
        { type: "bar", w: "62%" },
        { type: "pill" },
        { type: "bar", w: "34%" },
        { type: "bar", w: "76%" },
        { type: "kebab" },
      ],
    },
    steps: [
      {
        label: "Create the Want-Listing",
        text: "List the location, price and must-haves, the same way you list a property for sale.",
      },
      {
        label: "Listed across the network",
        text: "Every agent sees it, with the same exposure as a for-sale listing.",
      },
      {
        label: "Collaborate",
        text: "Matches surface automatically and agents reach out with introductions.",
      },
    ],
    ctaLabel: "Post a Want-Listing",
    learnHref: LEARN_HREF,
    footnote:
      "A Buyer Want-Listing describes the home, not the person. No client information is ever shown or shared.",
  },
  clients: {
    eyebrow: "My Clients",
    title: "Keep your clients organized in one place",
    subtitle:
      "Add a client and keep their Want-Listings, inventory and introductions organized in one place, so nothing falls through the cracks.",
    Glyph: PeopleGlyph,
    proofBadge: "3 Want-Listings",
    proofTone: "blue",
    proofLive: false,
    skeletonCols: {
      grid: "minmax(0, 1.5fr) minmax(0, 1.5fr) 92px 88px 96px 64px 64px 18px",
      cells: [
        { type: "avatar" },
        { type: "bar", w: "86%" },
        { type: "bar", w: "70%" },
        { type: "pill" },
        { type: "pill" },
        { type: "bar", w: "34%" },
        { type: "bar", w: "34%" },
        { type: "kebab" },
      ],
    },
    steps: [
      {
        label: "Add a client",
        text: "Your buyer or seller. Just a name to start.",
      },
      {
        label: "Everything in one place",
        text: "Their Want-Listings, inventory and introductions, organized together.",
      },
      {
        label: "Pick up anytime",
        text: "Open any client and see exactly where things stand.",
      },
    ],
    ctaLabel: "Add a Client",
    learnHref: LEARN_HREF,
    footnote: "Your clients stay private to you.",
  },
};

const renderSkelCell = (cell, key) => {
  switch (cell.type) {
    case "addr":
      return (
        <span className="de-sk-cell" key={key}>
          <span className="de-sk-thumb" />
          <span className="de-sk-bar" />
        </span>
      );
    case "loc":
      return (
        <span className="de-sk-cell" key={key}>
          <span className="de-sk-dot" />
          <span className="de-sk-bar" />
        </span>
      );
    case "avatar":
      return (
        <span className="de-sk-cell" key={key}>
          <span className="de-sk-avatar" />
          <span className="de-sk-bar" />
        </span>
      );
    case "pill":
      return <span className="de-sk-pill" key={key} />;
    case "tag":
      return <span className="de-sk-tag" key={key} />;
    case "kebab":
      return <span className="de-sk-kebab" key={key} />;
    default:
      return (
        <span
          className="de-sk-bar"
          style={cell.w ? { width: cell.w } : undefined}
          key={key}
        />
      );
  }
};

const DashboardEmptyState = ({ variant = "inventory", onCta }) => {
  const config = VARIANTS[variant] || VARIANTS.inventory;
  const {
    eyebrow,
    title,
    subtitle,
    Glyph,
    proofBadge,
    proofTone,
    proofLive,
    skeletonCols,
    steps,
    ctaLabel,
    learnHref,
    footnote,
  } = config;

  return (
    <div className="dashboard-empty-state">
      <div className="de-skeleton" aria-hidden="true">
        {Array.from({ length: SKELETON_ROW_COUNT }).map((_, ri) => (
          <div
            className="de-sk-row"
            style={{ gridTemplateColumns: skeletonCols.grid }}
            key={ri}
          >
            {skeletonCols.cells.map((cell, ci) => renderSkelCell(cell, ci))}
          </div>
        ))}
      </div>
      <div className="de-card">
        <div className="de-proof" aria-hidden="true">
          <div className="de-proof__snap">
            <span className={`de-proof__glyph de-proof__glyph--${proofTone}`}>
              <Glyph />
            </span>
            <span className="de-proof__bars">
              <span className="de-proof__bar" />
              <span className="de-proof__bar de-proof__bar--short" />
            </span>
            <span className={`de-proof__badge de-proof__badge--${proofTone}`}>
              {proofLive ? <span className="de-proof__pulse" /> : null}
              {proofBadge}
            </span>
          </div>
        </div>

        <span className="de-eyebrow">{eyebrow}</span>
        <h3 className="de-title">{title}</h3>
        <p className="de-subtitle">{subtitle}</p>

        <ol className="de-steps">
          {steps.map((step, i) => (
            <li className="de-step" key={i}>
              <span className="de-step__num">{i + 1}</span>
              <span className="de-step__label">{step.label}</span>
              <span className="de-step__text">{step.text}</span>
            </li>
          ))}
        </ol>

        <button type="button" className="btn btn-primary de-cta" onClick={onCta}>
          {ctaLabel}
        </button>

        {learnHref ? (
          <a
            className="de-learn"
            href={learnHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            See how it works
          </a>
        ) : null}

        {footnote ? <p className="de-footnote">{footnote}</p> : null}
      </div>
    </div>
  );
};

export default DashboardEmptyState;
